import { css, htmlArray } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";

type SoundSettingsType = {
    volume: number;
    muted: boolean;
};

const styles: string = css`
:host {
    display: block;
    position: relative;
    font-family: sans-serif;
}
/* round red settings button */
.settings-button {
    position: fixed;
    top: 10px;
    right: 50px;
    z-index: 9999;
    background: #e74c3c;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    padding: 6px;
    font-size: 16px;
    color: white;
    cursor: pointer;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease-in-out;
}
.settings-button:hover {
    background: #c0392b;
}
/* popup settings box */
.settings-popup {
    display: none;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(41, 35, 35, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 12px;
    padding: 20px;
    color: white;
    z-index: 10000;
    min-width: 300px;
    flex-direction: column;
    gap: 10px;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
}
.settings-popup.show {
    display: flex;
    opacity: 1;
}
.settings-popup button {
    padding: 6px 10px;
    border: none;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.15);
    color: white;
    cursor: pointer;
    transition: background 0.2s ease;
}
.settings-popup button:hover {
    background: rgba(255, 255, 255, 0.3);
}
.settings-popup .close-button {
    align-self: flex-end;
    font-size: 16px;
}
.settings-popup input[type="range"] {
    width: 100%;
}
audio {
    display: none;
}
`;

const audioFiles: string[] = [
    "/assets/audio/track1.mp3",
    "/assets/audio/track2.mp3",
    "/assets/audio/track3.mp3",
    "/assets/audio/track4.mp3",
    "/assets/audio/track5.mp3",
    "/assets/audio/track6.mp3",
];

let currentAudioIndex: number = Math.floor(Math.random() * audioFiles.length);
const sharedAudio: HTMLAudioElement = new Audio(audioFiles[currentAudioIndex]);

sharedAudio.preload = "auto";
sharedAudio.loop = false;

sharedAudio.addEventListener("ended", () => {
    currentAudioIndex = (currentAudioIndex + 1) % audioFiles.length;
    sharedAudio.src = audioFiles[currentAudioIndex];
    sharedAudio.play().catch((err: unknown) => {
        console.warn("[WARNING] Autoplay blocked on track change:", err);
    });
});

export class soundsettings extends HTMLElement {
    private slider: HTMLInputElement | null = null;
    private muteButton: HTMLButtonElement | null = null;
    private popup: HTMLElement | null = null;
    private readonly gameRouteService: GameRouteService = new GameRouteService();

    public constructor() {
        super();
        this.attachShadow({ mode: "open" });
    }

    public connectedCallback(): void {
        if (!this.shadowRoot) return;

        const elements: HTMLElement[] = htmlArray`
            <style>${styles}</style>
            <div class="settings-button">⚙️</div>
            <div class="settings-popup" id="settings-popup">
                <button class="close-button">✖️</button>
                <label>
                    🎵 Volume
                    <input type="range" id="volume-slider" min="0" max="1" step="0.01" />
                </label>
                <button id="mute-button">🔊 Mute</button>
            </div>
        `;

        this.shadowRoot.append(...elements);

        this.slider = this.shadowRoot.querySelector("#volume-slider");
        this.muteButton = this.shadowRoot.querySelector("#mute-button");
        this.popup = this.shadowRoot.querySelector("#settings-popup");

        const settingsButton: HTMLDivElement | null = this.shadowRoot.querySelector(".settings-button");
        const closeButton: HTMLButtonElement | null = this.shadowRoot.querySelector(".close-button");

        void this.loadSoundSettings();

        settingsButton?.addEventListener("click", () => {
            this.popup?.classList.add("show");
            void sharedAudio.play().catch(() => {
                // do nothing, probably blocked
            });
        });

        closeButton?.addEventListener("click", () => {
            this.popup?.classList.remove("show");
        });

        document.addEventListener("keydown", (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                this.popup?.classList.remove("show");
            }
        });

        this.slider?.addEventListener("input", () => {
            if (!this.slider) return;
            const newVolume: number = parseFloat(this.slider.value);

            sharedAudio.volume = newVolume;

            // Optional: toggle mute if user drags to/from 0
            if (newVolume === 0) {
                sharedAudio.muted = true;
            }
            else if (sharedAudio.muted) {
                sharedAudio.muted = false;
            }

            this.updateMuteButton();
            void this.saveSoundSettings(sharedAudio.volume, sharedAudio.muted);
        });

        this.muteButton?.addEventListener("click", () => {
            if (!this.slider || !this.muteButton) return;

            sharedAudio.muted = !sharedAudio.muted;

            // Reflect mute visually (but don't override volume itself)
            if (sharedAudio.muted) {
                this.slider.value = "0";
            }
            else {
                this.slider.value = String(sharedAudio.volume);
            }

            this.updateMuteButton();
            void this.saveSoundSettings(sharedAudio.volume, sharedAudio.muted);
        });

        this.updateMuteButton();
    }

    private async loadSoundSettings(): Promise<void> {
        try {
            const settings: SoundSettingsType = await this.gameRouteService.getSoundSettings();

            sharedAudio.volume = settings.volume;
            sharedAudio.muted = settings.muted;

            if (this.slider) {
                this.slider.value = settings.muted ? "0" : String(settings.volume);
            }

            if (sharedAudio.paused) {
                await sharedAudio.play().catch((err: unknown) => {
                    if (err instanceof Error) {
                        console.warn("[WARNING] Autoplay blocked:", err.message);
                    }
                });
            }

            this.updateMuteButton();
        }
        catch (error: unknown) {
            if (error instanceof Error) {
                console.error("Failed to load sound settings:", error.message);
            }
        }
    }

    private async saveSoundSettings(volume: number, muted: boolean): Promise<void> {
        try {
            await this.gameRouteService.setSoundSettings(volume, muted);
        }
        catch (error: unknown) {
            if (error instanceof Error) {
                console.error("Failed to save sound settings:", error.message);
            }
        }
    }

    private updateMuteButton(): void {
        if (!this.muteButton) return;
        this.muteButton.textContent = sharedAudio.muted ? "🔇 Unmute" : "🔊 Mute";
    }
}
